import moment from 'moment';
import { AssessmentQuestionStatusById, QuestionStatusById } from '../app/store/questionnaireAnswerSlice';
import { Assessment, AssessmentSurvey, AssessorQuestionAnswer, PIDetails, SubRemediation, WillNotRemediateReasons } from '../interfaces/assessment.interface';
import { Question } from '../interfaces/question.interface';
import { REMEDIATION_OPTIONS } from '../services/surveyHelpers';
import { Survey } from '../interfaces/survey.interface';
import { File } from '../interfaces/questionAnswer.interface';

export const getRemediationCounts = ({
  assessmentQuestionsStatus,
  assessment,
  questionStatus,
  questions,
}: {
  assessmentQuestionsStatus: AssessmentQuestionStatusById,
  assessment: Assessment,
  questionStatus: QuestionStatusById,
  questions?: Question[],
}) => {
  let bestPracticeRemediationCount = 0;
  let implementationGuidanceRemediationCount = 0;
  if (!assessmentQuestionsStatus || !assessment) return { bestPracticeRemediationCount, implementationGuidanceRemediationCount };

  Object.keys(assessmentQuestionsStatus).forEach(assAnsId => {
    const { status, vendorStatus, question } = assessmentQuestionsStatus[assAnsId];
    if (!status || ['remediation', 'priority'].indexOf(status) === -1  || (vendorStatus && [REMEDIATION_OPTIONS.remediated].indexOf(vendorStatus) !== -1)) return;
    let isBpQuestion = false;
    if (typeof question !== 'number') {
      isBpQuestion = question.isBestPractice;
    } else {
      if (questionStatus[question as number]) isBpQuestion = questionStatus[question as number].question.isBestPractice;
      if (questionStatus[question as number] && questions) isBpQuestion = questions.find(q => q.id === (question as number)) ? questions.find(q => q.id === (question as number))!.isBestPractice : false; //Need extra test on questions here to support dev submit for assessment before all questions answered
    }

    if (isBpQuestion) bestPracticeRemediationCount++;
    if (!isBpQuestion) implementationGuidanceRemediationCount++;
  });
  return { bestPracticeRemediationCount, implementationGuidanceRemediationCount };
};

// TPE-3041: true when a Remediated/Will Remediate Later sub-remediation is missing Additional Details
export const hasSubRemMissingDetails = (subRemediations: SubRemediation[]): boolean =>
  subRemediations.some((subRem) =>
    [REMEDIATION_OPTIONS.remediated, REMEDIATION_OPTIONS.willRemediateLater].includes(subRem.remediationPlan || '') &&
    !subRem.remediationComment?.trim());

export const buildFormattedSubRemediations = (subRemediations: SubRemediation[]) => {
  return subRemediations?.map((subRem) => ({
    ...subRem,
    attachments: subRem.attachments?.map((file) =>
      typeof file === 'number' ? file : (file as File).id,
    ),
  })) || [];
};

export const buildAssessorAnswer = ({
  question,
  prop,
  data,
  questionCtrlAns,
  assessmentQuestionsStatus,
  assessorFinding,
  assessorAnswer,
  createdSubRemediations,
  vendorStatus,
  remediationComment,
  willNotRemediateReason,
  remediationDate,
  assessment,
  survey,
  existingCommentIds,
}: {
  question: Question;
  existingCommentIds: number[];
  prop: any;
  data: any;
  assessmentQuestionsStatus: AssessmentQuestionStatusById;
  questionCtrlAns?: { [key: number]: PIDetails };
  assessorFinding?: string;
  assessorAnswer?: string;
  createdSubRemediations?: SubRemediation[];
  vendorStatus?: string;
  remediationComment?: string;
  willNotRemediateReason?: WillNotRemediateReasons;
  remediationDate: string | null;
  assessment?: Assessment;
  survey?: Survey;
}) => {
  return {
    question,
    status: prop.status,
    comment: prop.comment,
    comments: prop.comment && prop.comment?.id
      ? [
        ...existingCommentIds,
        prop.comment.id]
      : existingCommentIds,
    auditLog: prop.auditLog,
    auditLogEntries: prop.auditLog
      ? [prop.auditLog.id]
      : assessmentQuestionsStatus[question.id]?.auditLogEntries,
    assessorFinding,
    assessorAnswer,
    answer: assessorAnswer,
    remediationQuestionAnswers: createdSubRemediations
      ? createdSubRemediations
      : prop.subRemediations?.reduce((acc: any[], curr: SubRemediation) => {
        if (curr.answer === undefined) return acc;
        acc.push({
          answer: curr.answer,
          assessorAnswer: assessmentQuestionsStatus && assessmentQuestionsStatus[question.id]?.id,
          remediationComment: curr.remediationComment,
          remediationDate: curr.remediationDate,
          remediationPlan: curr.remediationPlan,
          willNotRemediateReason: curr.willNotRemediateReason,
          attachments: [],
        });
        return acc;
      }, []),
    vendorStatus,
    remediationComment,
    willNotRemediateReason,
    assessorQuestionAnswers:
      data && data[question.id]
        ? (data[question.id]?.assessorQuestionAnswers ?? []).map((value: AssessorQuestionAnswer) => ({
          id: value.id,
          answer: value.answer,
          comment: value.comment,
          isImplemented: value.isImplemented,
          compensatingOrNotApplicable: value.compensatingOrNotApplicable,
        })) as AssessorQuestionAnswer[]
        : Object.entries(questionCtrlAns ?? {}).map(([key, value]: any) => ({
          answer: parseInt(key),
          comment: value.comment || '',
          isImplemented: value.isImplemented || false,
          compensatingOrNotApplicable: value.compensatingOrNotApplicable || '',
        })) as AssessorQuestionAnswer[],
    contentOwnerPriority: prop.contentOwnerPriority,
    remediationDate: remediationDate
      ? moment(remediationDate).utc().format('YYYY-MM-DDTHH:mm:ssZZ')
      : undefined,
    attachments: prop.attachments as number[],
    assessmentSurvey: assessment
      ? (assessment.surveys as AssessmentSurvey[]).find((asssur) =>
        typeof asssur.survey === 'number'
          ? asssur.survey === survey?.id
          : asssur.survey.id === survey?.id,
      )?.id
      : undefined,
  };
};